import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChecklistList } from "@/components/ChecklistList";
import { EvidencePanel } from "@/components/EvidencePanel";
import { EquipmentReadList } from "@/components/EquipmentLines";
import { SvcAcTerminals } from "@/components/wo/AcTerminals";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const RESULT = { completada: "Terminado", completada_obs: "Terminado con observaciones", pendiente: "Pendiente" };
const sel = "h-10 w-full rounded-md border border-input bg-background px-3 text-sm";

const AddService = ({ woId, onDone }) => {
  const { meta } = useAuth();
  const [f, setF] = useState({ business_unit_code: "", service_type_code: "", scope: "", warranty_months: "", billing_notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/work-orders/${woId}/services`, { ...f, warranty_months: f.warranty_months === "" ? null : Number(f.warranty_months) });
      onDone(data);
      setF({ business_unit_code: "", service_type_code: "", scope: "", warranty_months: "", billing_notes: "" });
      toast.success("Servicio agregado a la OS");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-3 rounded-lg border border-dashed border-border p-4 sm:grid-cols-2" data-testid="extra-service-add-form">
      <select className={sel} value={f.business_unit_code} onChange={(e) => set("business_unit_code", e.target.value)} data-testid="extra-service-bu">
        <option value="">Unidad de negocio…</option>
        {(meta?.business_units || []).map((b) => <option key={b.code} value={b.code}>{b.name}</option>)}
      </select>
      <select className={sel} value={f.service_type_code} onChange={(e) => set("service_type_code", e.target.value)} data-testid="extra-service-type">
        <option value="">Tipo de servicio…</option>
        {(meta?.service_types || []).map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
      </select>
      <Textarea className="sm:col-span-2" rows={2} placeholder="Descripción y alcance de este servicio" value={f.scope} onChange={(e) => set("scope", e.target.value)} data-testid="extra-service-scope" />
      <Input type="number" min={0} placeholder="Garantía (meses, informativa)" value={f.warranty_months} onChange={(e) => set("warranty_months", e.target.value)} data-testid="extra-service-warranty" />
      <Input placeholder="Tarifa / facturación (nota informativa)" value={f.billing_notes} onChange={(e) => set("billing_notes", e.target.value)} data-testid="extra-service-billing" />
      <div className="sm:col-span-2 flex justify-end"><Button onClick={save} disabled={busy || !f.business_unit_code || !f.service_type_code} data-testid="extra-service-add-submit"><Plus className="mr-1 h-4 w-4" />Agregar servicio</Button></div>
    </div>
  );
};

const ServiceCard = ({ wo, s, n, canManage, canExec, onList, onOne }) => {
  const { meta } = useAuth();
  const prio = (meta?.priorities || []).find((p) => p.code === s.priority)?.label || s.priority;
  const [notes, setNotes] = useState(s.outcome?.notes || "");
  const toggle = async (item, patch = { checked: !item.checked }) => {
    try {
      const { data } = await api.patch(`/work-orders/${wo.id}/services/${s.id}/checklist/${item.id}`, patch);
      onOne({ ...s, checklist: data });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const finish = async (result) => {
    try { onOne((await api.post(`/work-orders/${wo.id}/services/${s.id}/finish`, { result, notes })).data); toast.success("Servicio actualizado"); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm("¿Quitar este servicio de la OS? Su historial se conserva.")) return;
    try { onList((await api.delete(`/work-orders/${wo.id}/services/${s.id}`)).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const done = ["completada", "completada_obs"].includes(s.outcome?.result);
  return (
    <section className="space-y-4 rounded-xl border border-border p-4" data-testid={`extra-service-${n}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-base font-semibold">Servicio {n}: {s.service_type_name} · {s.business_unit_name}</h3>
        <span className="flex items-center gap-2">
          <span className={done ? "text-xs font-semibold text-emerald-400" : "text-xs font-semibold text-orange-400"} data-testid={`extra-service-${n}-status`}>{RESULT[s.outcome?.result] || "Sin terminar"}</span>
          {canManage && <Button size="sm" variant="ghost" onClick={remove} data-testid={`extra-service-${n}-remove`}><Trash2 className="h-4 w-4" /></Button>}
        </span>
      </div>
      {s.scope && <p className="whitespace-pre-wrap text-sm">{s.scope}</p>}
      {(s.priority || s.notes) && <p className="whitespace-pre-wrap text-xs text-muted-foreground" data-testid={`extra-service-${n}-notes-info`}>{s.priority && `Prioridad: ${prio}`}{s.priority && s.notes && " · "}{s.notes && `Observaciones para el técnico: ${s.notes}`}</p>}
      {(s.warranty_months != null || s.billing_notes) && <p className="text-xs text-muted-foreground">Garantía: {s.warranty_months ?? "—"} meses · {s.billing_notes || "Sin nota de facturación"}</p>}
      {s.equipment?.length > 0 && <EquipmentReadList lines={s.equipment} />}
      <SvcAcTerminals wo={wo} s={s} editable={canExec && !done} onSvc={onOne} n={n} />
      <ChecklistList items={s.checklist?.items} onToggle={canExec && !done ? toggle : null} tri={s.checklist?.mode === "tri"} />
      <EvidencePanel woId={wo.id} serviceId={s.id} editable={canExec && !done} />
      {canExec && (
        <div className="space-y-2">
          <Textarea rows={2} placeholder="Observaciones o motivo del pendiente" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid={`extra-service-${n}-notes`} />
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={() => finish("pendiente")} data-testid={`extra-service-${n}-pending`}>Pendiente</Button>
            <Button variant="outline" onClick={() => finish("completada_obs")} data-testid={`extra-service-${n}-finish-obs`}>Terminar con observaciones</Button>
            <Button onClick={() => finish("completada")} data-testid={`extra-service-${n}-finish`}><CheckCircle2 className="mr-1 h-4 w-4" />Terminar servicio</Button>
          </div>
        </div>
      )}
    </section>
  );
};

export const ExtraServices = ({ wo, setWo, canManage, canExec }) => {
  const list = (wo.extra_services || []).filter((s) => !s.removed);
  if (!list.length && !canManage) return null;
  const onList = (data) => setWo((w) => ({ ...w, extra_services: data }));
  const onOne = (svc) => setWo((w) => ({ ...w, extra_services: (w.extra_services || []).map((x) => (x.id === svc.id ? svc : x)) }));
  return (
    <div className="space-y-4" data-testid="extra-services">
      <p className="text-xs text-muted-foreground">Cada servicio adicional tiene su propio checklist, fotos y resultado. La OS solo se termina y se cierra cuando todos están terminados.</p>
      {list.map((s, i) => <ServiceCard key={s.id} wo={wo} s={s} n={i + 2} canManage={canManage} canExec={canExec} onList={onList} onOne={onOne} />)}
      {canManage && <AddService woId={wo.id} onDone={onList} />}
    </div>
  );
};
